import { useState, useEffect, useCallback } from 'react';
import { Truck, MapPin, Calendar, Clock, Package, CheckCircle, XCircle, Navigation, Plus, ChevronLeft, ChevronRight, FileText, Filter, Wand2, ArrowUp, ArrowDown, Save, Undo2 } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { logger } from '../utils/logger';
import BonsEnfactModal from '../components/livraison/BonsEnfactModal';
import CommandesIrsiaModal from '../components/livraison/CommandesIrsiaModal';
import ControlePeseeModal from '../components/livraison/ControlePeseeModal';
import { etiquetteLivraisonZpl } from '../utils/etiquetteLivraison';
import { hardwareBridgeZebra } from '../services/printers/HardwareBridgeAdapter';
import { NouvelleTourneeModal, AjouterCommandesTournee } from '../components/livraison/CommandesAPlacer';

/** Un arrêt de la tournée. `position` fait foi pour l'ordre de passage. */
interface DeliveryStop {
  id: string;
  status?: string;
  position: number;
  /** Heure de passage estimée, calculée par le serveur sur le trajet réel. */
  estimated_time?: string | null;
  actual_time?: string | null;
  order: {
    id: string;
    order_number: string;
    customer_name: string;
    customer_phone?: string;
    total_cents: number;
    metadata?: any;
    delivery_address?: {
      street: string;
      city: string;
      postal_code: string;
    };
  };
}

interface DeliveryRoute {
  id: string;
  delivery_date: string; // Date de livraison (vient de Prisma)
  status: string; // 'ASSIGNED' | 'STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'
  vendor_id?: string | null;
  /** Le livreur est un vendeur portant le poste « Livraison ». */
  courier?: { id: string; name: string } | null;
  /**
   * Ancienne entité livreur. Vaut `null` sur toute tournée créée depuis la
   * refonte : elle ne sert plus qu'à afficher un nom sur l'historique.
   */
  driver?: {
    id: string;
    name: string;
    email: string;
  } | null;
  orders: DeliveryStop[];
  route?: {
    /** En KILOMÈTRES (le serveur ne stocke plus de mètres ici). */
    total_distance: number;
    /** En minutes. */
    total_duration: number;
  };
  created_at: string;
}

interface Courier {
  id: string;
  name: string;
  role: string;
}

interface DeliveryStats {
  todayTotal: number;
  todayCompleted: number;
  todayPending: number;
  todayInProgress: number;
}

interface ReoptimizeResult {
  ordered: number;
  unlocated: number;
  distance_km: number;
  duration_minutes: number;
}

/** Nom du livreur : le vendeur d'abord, l'ancienne entité en dernier recours. */
function nomDuLivreur(route: DeliveryRoute): string | null {
  return route.courier?.name || route.driver?.name || null;
}

/** « 14:35 » à partir d'une date ISO ; rien si la date est absente ou illisible. */
function heure(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });
}

export default function CustomerDeliveries() {
  const { tenant: currentTenant } = useTenant();
  const [routes, setRoutes] = useState<DeliveryRoute[]>([]);
  const [couriers, setCouriers] = useState<Courier[]>([]);
  const [stats, setStats] = useState<DeliveryStats>({
    todayTotal: 0,
    todayCompleted: 0,
    todayPending: 0,
    todayInProgress: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  // Bons de livraison Enfact du jour affiché → livraisons (tournée B2B du lundi)
  const [bonsEnfactOuvert, setBonsEnfactOuvert] = useState(false);
  const [irsiaOuvert, setIrsiaOuvert] = useState(false);
  // Nouvelle tournée : choisir ses commandes et son livreur (15/10/2026).
  const [nouvelleTourneeOuverte, setNouvelleTourneeOuverte] = useState(false);
  const [arretEnCours, setArretEnCours] = useState<string | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<DeliveryRoute | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [courierFilter, setCourierFilter] = useState<string>('all');

  // Livreur pressenti pour la prochaine génération de tournée.
  const [newRouteCourierId, setNewRouteCourierId] = useState<string>('');

  // Ordre de passage remanié à la main : gardé en local tant qu'il n'est pas
  // enregistré, pour qu'on puisse déplacer plusieurs arrêts avant de valider —
  // et revenir en arrière sans avoir rien écrit.
  const [draftStops, setDraftStops] = useState<DeliveryStop[] | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [reoptimizing, setReoptimizing] = useState(false);
  const [reoptimizeResult, setReoptimizeResult] = useState<ReoptimizeResult | null>(null);

  const API_URL = import.meta.env.VITE_API_URL;
  // Contrôle pesé avant le départ (bons Enfact, 02/11/2026) : commande ouverte.
  const [controlePesee, setControlePesee] = useState<{ orderId: string; deliveryId: string } | null>(null);

  /**
   * Toutes les routes de livraison sont derrière le garde JWT du personnel.
   * L'en-tête d'autorisation manquait sur les cinq appels de cette page : elle
   * ne pouvait donc rien faire d'autre que rester vide.
   */
  const apiFetch = useCallback(
    async (path: string, init: RequestInit = {}) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Tenant-ID': currentTenant?.id || '',
        ...(init.headers as Record<string, string> | undefined),
      };
      const token = localStorage.getItem('auth_token');
      if (token) headers.Authorization = `Bearer ${token}`;
      return fetch(`${API_URL}${path}`, { ...init, headers });
    },
    [API_URL, currentTenant?.id],
  );

  /** Message d'erreur lisible, quelle que soit la forme de la réponse. */
  const messageErreur = async (response: Response, defaut: string): Promise<string> => {
    try {
      const body = await response.json();
      const message = body?.message;
      return Array.isArray(message) ? message.join(', ') : message || defaut;
    } catch {
      return defaut;
    }
  };

  const loadRoutes = useCallback(async () => {
    if (!currentTenant?.id) return;
    try {
      setLoading(true);
      const response = await apiFetch(`/customer-deliveries?date=${selectedDate}`);

      if (response.ok) {
        const data: DeliveryRoute[] = await response.json();
        setRoutes(data);

        const todayRoutes = data.filter(r =>
          r.delivery_date && r.delivery_date.toString().startsWith(selectedDate)
        );

        setStats({
          todayTotal: todayRoutes.length,
          todayCompleted: todayRoutes.filter(r => r.status === 'COMPLETED').length,
          todayPending: todayRoutes.filter(r => r.status === 'ASSIGNED').length,
          todayInProgress: todayRoutes.filter(r => r.status === 'STARTED' || r.status === 'IN_PROGRESS').length,
        });
      } else {
        logger.error('[CustomerDeliveries] API Error:', response.status);
        setRoutes([]);
      }
    } catch (error) {
      logger.error('[CustomerDeliveries] Erreur chargement tournées:', error);
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, currentTenant?.id, selectedDate]);

  /** Vendeurs autorisés à livrer (poste « Livraison », ou ADMIN/MANAGER). */
  const loadCouriers = useCallback(async () => {
    if (!currentTenant?.id) return;
    try {
      const response = await apiFetch('/courier/workers');
      if (response.ok) setCouriers(await response.json());
    } catch (error) {
      logger.error('[CustomerDeliveries] Erreur chargement livreurs:', error);
    }
  }, [apiFetch, currentTenant?.id]);

  useEffect(() => {
    if (currentTenant?.id) loadRoutes();
  }, [loadRoutes, currentTenant?.id]);

  useEffect(() => {
    if (currentTenant?.id) loadCouriers();
  }, [loadCouriers, currentTenant?.id]);

  /**
   * Vidanges EPS d'une livraison B2B (26/10/2026) : corriger le nombre, ou réécrire
   * sur le bon Enfact après un échec. Le serveur écrit la légende et la ligne
   * négative « Retour vidanges » sur le bon, puis le relit et le contrôle.
   */
  const vidangesEps = async (orderId: string, deliveryId: string, actuel: number | null, reecrire = false) => {
    let corps: string | undefined;
    let chemin = `/orders/bons-enfact/${orderId}/reporter-vidanges`;
    let methode = 'POST';
    if (!reecrire) {
      const saisi = window.prompt('Vidanges EPS reprises (caisses rendues) :', actuel !== null ? String(actuel) : '');
      if (saisi === null) return;
      const n = Number(saisi.trim());
      if (!Number.isInteger(n) || n < 0 || n > 99) { alert('Un nombre entier de 0 à 99.'); return; }
      chemin = `/orders/bons-enfact/${orderId}/vidanges-eps`;
      methode = 'PATCH';
      corps = JSON.stringify({ nombre: n });
    }
    try {
      const r = await apiFetch(chemin, { method: methode, headers: { 'Content-Type': 'application/json' }, body: corps });
      const d = await r.json().catch(() => ({}));
      alert(r.ok && d?.ok ? `✅ ${d.message}` : `⚠️ ${d?.message || 'Report impossible'}`);
    } finally {
      await refreshSelected(deliveryId);
    }
  };

  /**
   * Caisses EPS préparées d'une livraison B2B (26/10/2026) : fixe le nombre de colis
   * (étiquettes) et écrit la ligne « Vidanges » positive sur le bon Enfact.
   */
  const caissesPreparees = async (orderId: string, deliveryId: string, actuel: number | null) => {
    const saisi = window.prompt('Caisses EPS préparées pour ce point :', actuel !== null ? String(actuel) : '');
    if (saisi === null) return;
    const n = Number(saisi.trim());
    if (!Number.isInteger(n) || n < 0 || n > 99) { alert('Un nombre entier de 0 à 99.'); return; }
    try {
      const r = await apiFetch(`/orders/bons-enfact/${orderId}/caisses-preparees`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre: n }),
      });
      const d = await r.json().catch(() => ({}));
      alert(r.ok && d?.ok ? `✅ ${d.message}` : `⚠️ ${d?.message || 'Écriture impossible'}`);
    } finally {
      await refreshSelected(deliveryId);
    }
  };

  /** Recharge la tournée ouverte : les heures de passage ont pu bouger. */
  const refreshSelected = async (deliveryId: string) => {
    try {
      const response = await apiFetch(`/customer-deliveries/${deliveryId}`);
      if (response.ok) {
        const fresh: DeliveryRoute = await response.json();
        setSelectedRoute(fresh);
        setDraftStops(null);
      }
    } catch (error) {
      logger.error('[CustomerDeliveries] Erreur rechargement tournée:', error);
    }
  };

  /**
   * Une étiquette Zebra par livraison (19/10/2026), dans l'ordre de passage :
   * QR unique pour tous les colis de l'arrêt, nombre de colis, code de secours.
   * Imprime sur la Zebra branchée sur CE poste (le bridge local).
   */
  const [impressionEtiquettes, setImpressionEtiquettes] = useState(false);
  /** Toute la tournée, ou un seul arrêt (`seulArretId`) : étiquette abîmée, colis ajouté (03/11/2026). */
  const imprimerEtiquettes = async (route: DeliveryRoute, seulArretId?: string) => {
    const tous = [...route.orders].sort((a, b) => a.position - b.position);
    // Le rang imprimé reste celui de l'arrêt dans la tournée, même pour une seule étiquette.
    const arrets = tous
      .map((a, i) => ({ a, rang: i + 1 }))
      .filter(({ a }) => !seulArretId || a.id === seulArretId);
    if (arrets.length === 0) return;
    const question = seulArretId
      ? `Imprimer l'étiquette de ${arrets[0].a.order.customer_name} sur la Zebra de ce poste ?`
      : `Imprimer ${arrets.length} étiquette${arrets.length > 1 ? 's' : ''} sur la Zebra de ce poste ?`;
    if (!confirm(question)) return;
    setImpressionEtiquettes(true);
    let ok = 0;
    let echec = '';
    for (const { a, rang } of arrets) {
      const zpl = etiquetteLivraisonZpl({
        orderNumber: a.order.order_number,
        nom: a.order.customer_name,
        colis: Number(a.order.metadata?.colis) || 1,
        rang,
      });
      const r = await hardwareBridgeZebra.printZPL(zpl).catch((e: any) => ({ success: false, message: e?.message || 'Zebra injoignable' }));
      if (r.success) ok++;
      else { echec = r.message; break; }
    }
    setImpressionEtiquettes(false);
    alert(echec ? `❌ ${ok}/${arrets.length} imprimée(s). Zebra : ${echec}` : `✅ ${ok} étiquette${ok > 1 ? 's' : ''} imprimée${ok > 1 ? 's' : ''}`);
  };

  /** Retire un arrêt (la commande redevient « prête ») ou le passe dans une autre tournée du jour. */
  const modifierArret = async (routeId: string, stopId: string, versRouteId: string | null) => {
    setArretEnCours(stopId);
    try {
      const response = versRouteId
        ? await apiFetch(`/customer-deliveries/${routeId}/stops/${stopId}/move`, {
            method: 'PUT',
            body: JSON.stringify({ to_delivery_id: versRouteId }),
          })
        : await apiFetch(`/customer-deliveries/${routeId}/stops/${stopId}`, { method: 'DELETE' });
      if (!response.ok) {
        alert(`❌ ${await messageErreur(response, 'modification impossible')}`);
        return;
      }
      await refreshSelected(routeId);
      loadRoutes();
    } catch (error) {
      logger.error('Erreur modification arrêt:', error);
      alert('❌ Erreur lors de la modification de la tournée');
    } finally {
      setArretEnCours(null);
    }
  };

  const startDelivery = async (deliveryId: string) => {
    if (!confirm('Démarrer cette tournée de livraison ?')) return;

    try {
      const response = await apiFetch(`/customer-deliveries/${deliveryId}/start`, { method: 'PUT' });

      if (response.ok) {
        alert('✅ Tournée démarrée');
        loadRoutes();
        setSelectedRoute(null);
      } else {
        alert(`❌ Erreur: ${await messageErreur(response, 'démarrage impossible')}`);
      }
    } catch (error) {
      logger.error('Erreur démarrage tournée:', error);
      alert('❌ Erreur lors du démarrage de la tournée');
    }
  };

  const completeDelivery = async (deliveryId: string) => {
    if (!confirm('Marquer cette tournée comme terminée ?')) return;

    try {
      const response = await apiFetch(`/customer-deliveries/${deliveryId}/complete`, { method: 'PUT' });

      if (response.ok) {
        alert('✅ Tournée terminée');
        loadRoutes();
        setSelectedRoute(null);
      } else {
        alert(`❌ Erreur: ${await messageErreur(response, 'clôture impossible')}`);
      }
    } catch (error) {
      logger.error('Erreur complétion tournée:', error);
      alert('❌ Erreur lors de la complétion de la tournée');
    }
  };

  const cancelDelivery = async (deliveryId: string) => {
    const reason = prompt('Raison de l\'annulation (optionnel):');
    if (reason === null) return; // Bouton « Annuler » de la boîte de dialogue

    try {
      const response = await apiFetch(`/customer-deliveries/${deliveryId}`, {
        method: 'DELETE',
        body: JSON.stringify({ reason }),
      });

      if (response.ok) {
        alert('✅ Tournée annulée');
        loadRoutes();
        setSelectedRoute(null);
      } else {
        alert(`❌ Erreur: ${await messageErreur(response, 'annulation impossible')}`);
      }
    } catch (error) {
      logger.error('Erreur annulation tournée:', error);
      alert('❌ Erreur lors de l\'annulation de la tournée');
    }
  };

  /** Confie (ou retire) la tournée à un vendeur. */
  const assignCourier = async (deliveryId: string, vendorId: string) => {
    try {
      const response = await apiFetch(`/customer-deliveries/${deliveryId}/courier`, {
        method: 'PUT',
        body: JSON.stringify({ vendor_id: vendorId || null }),
      });

      if (!response.ok) {
        alert(`❌ Erreur: ${await messageErreur(response, 'affectation impossible')}`);
        return;
      }
      await refreshSelected(deliveryId);
      loadRoutes();
    } catch (error) {
      logger.error('Erreur affectation livreur:', error);
      alert('❌ Erreur lors de l\'affectation du livreur');
    }
  };

  /** Recalcule l'ordre de passage des arrêts qu'il reste à faire. */
  const reoptimize = async (deliveryId: string) => {
    setReoptimizing(true);
    setReoptimizeResult(null);
    try {
      const response = await apiFetch(`/customer-deliveries/${deliveryId}/reoptimize`, { method: 'POST' });

      if (!response.ok) {
        alert(`❌ Erreur: ${await messageErreur(response, 'réoptimisation impossible')}`);
        return;
      }
      setReoptimizeResult(await response.json());
      await refreshSelected(deliveryId);
      loadRoutes();
    } catch (error) {
      logger.error('Erreur réoptimisation:', error);
      alert('❌ Erreur lors de la réoptimisation');
    } finally {
      setReoptimizing(false);
    }
  };

  /** Enregistre l'ordre remanié à la main. La liste envoyée est complète. */
  const saveStopOrder = async (deliveryId: string, stops: DeliveryStop[]) => {
    setSavingOrder(true);
    try {
      const response = await apiFetch(`/customer-deliveries/${deliveryId}/stops/order`, {
        method: 'PUT',
        body: JSON.stringify({ stop_ids: stops.map(s => s.id) }),
      });

      if (!response.ok) {
        alert(`❌ Erreur: ${await messageErreur(response, 'ordre refusé')}`);
        return;
      }
      const fresh: DeliveryRoute = await response.json();
      setSelectedRoute(fresh);
      setDraftStops(null);
      setReoptimizeResult(null);
      loadRoutes();
    } catch (error) {
      logger.error('Erreur enregistrement ordre:', error);
      alert('❌ Erreur lors de l\'enregistrement de l\'ordre');
    } finally {
      setSavingOrder(false);
    }
  };

  const moveStop = (index: number, direction: -1 | 1) => {
    const base = draftStops ?? selectedRoute?.orders ?? [];
    const cible = index + direction;
    if (cible < 0 || cible >= base.length) return;

    const copie = [...base];
    [copie[index], copie[cible]] = [copie[cible], copie[index]];
    setDraftStops(copie);
  };

  const openRoute = (route: DeliveryRoute) => {
    setSelectedRoute(route);
    setDraftStops(null);
    setReoptimizeResult(null);
  };

  const closeRoute = () => {
    setSelectedRoute(null);
    setDraftStops(null);
    setReoptimizeResult(null);
  };

  const changeDate = (days: number) => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + days);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const exportRoutePDF = (route: DeliveryRoute) => {
    // Créer le contenu HTML pour l'impression
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('❌ Impossible d\'ouvrir la fenêtre d\'impression. Vérifiez les paramètres de votre navigateur.');
      return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Feuille de route - Tournée ${route.id.slice(0, 8)}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #16a34a; }
          .header { border-bottom: 2px solid #16a34a; padding-bottom: 10px; margin-bottom: 20px; }
          .info-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px; }
          .info-box { background: #f3f4f6; padding: 10px; border-radius: 5px; }
          .info-label { font-size: 12px; color: #6b7280; }
          .info-value { font-size: 18px; font-weight: bold; margin-top: 5px; }
          .stop { border: 1px solid #e5e7eb; padding: 15px; margin-bottom: 10px; border-radius: 5px; page-break-inside: avoid; }
          .stop-number { display: inline-block; background: #16a34a; color: white; width: 30px; height: 30px; border-radius: 50%; text-align: center; line-height: 30px; font-weight: bold; margin-right: 10px; }
          .stop-header { font-size: 16px; font-weight: bold; margin-bottom: 5px; }
          .stop-address { color: #6b7280; margin-bottom: 5px; }
          .stop-details { font-size: 14px; color: #374151; }
          .signature-box { border: 1px solid #e5e7eb; padding: 20px; margin-top: 10px; min-height: 80px; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🚚 Feuille de Route - Tournée #${route.id.slice(0, 8)}</h1>
          <p><strong>Date:</strong> ${new Date(route.delivery_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
          <p><strong>Livreur:</strong> ${nomDuLivreur(route) || 'Non assigné'}</p>
        </div>

        <div class="info-grid">
          <div class="info-box">
            <div class="info-label">Nombre d'arrêts</div>
            <div class="info-value">${route.orders.length}</div>
          </div>
          <div class="info-box">
            <div class="info-label">Distance totale</div>
            <div class="info-value">${route.route?.total_distance ? route.route.total_distance.toFixed(1) + ' km' : 'N/A'}</div>
          </div>
          <div class="info-box">
            <div class="info-label">Durée estimée</div>
            <div class="info-value">${route.route?.total_duration ? Math.round(route.route.total_duration) + ' min' : 'N/A'}</div>
          </div>
          <div class="info-box">
            <div class="info-label">Valeur totale</div>
            <div class="info-value">${(route.orders.reduce((sum, o) => sum + Number(o.order.total_cents), 0) / 100).toFixed(2)} €</div>
          </div>
        </div>

        <h2>📍 Liste des arrêts</h2>
        ${route.orders.map((orderItem, index) => `
          <div class="stop">
            <div>
              <span class="stop-number">${index + 1}</span>
              <span class="stop-header">${orderItem.order.customer_name}</span>
              <span style="color: #6b7280; font-size: 12px;">#${orderItem.order.order_number}</span>
              ${heure(orderItem.estimated_time) ? `<span style="float: right; font-weight: bold;">~ ${heure(orderItem.estimated_time)}</span>` : ''}
            </div>
            ${orderItem.order.delivery_address ? `
              <div class="stop-address">
                📍 ${orderItem.order.delivery_address.street}, ${orderItem.order.delivery_address.postal_code} ${orderItem.order.delivery_address.city}
              </div>
            ` : ''}
            <div class="stop-details">
              <strong>Montant:</strong> ${(Number(orderItem.order.total_cents) / 100).toFixed(2)} €
              ${orderItem.order.customer_phone ? ` | <strong>Tél:</strong> ${orderItem.order.customer_phone}` : ''}
            </div>
            <div class="signature-box">
              <strong>Signature client:</strong>
            </div>
          </div>
        `).join('')}

        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
          <p><strong>Heure de départ:</strong> ___:___</p>
          <p><strong>Heure d'arrivée:</strong> ___:___</p>
          <p><strong>Remarques:</strong></p>
          <div style="border: 1px solid #e5e7eb; padding: 10px; min-height: 100px; margin-top: 10px;"></div>
        </div>

        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Filtrer les routes
  const filteredRoutes = routes.filter(route => {
    if (statusFilter !== 'all' && route.status !== statusFilter) return false;
    if (courierFilter !== 'all') {
      const id = route.courier?.id || route.vendor_id || route.driver?.id || '';
      if (id !== courierFilter) return false;
    }
    return true;
  });

  // Livreurs proposés au filtre : ceux du jour, plus ceux qui pourraient livrer.
  const filterCouriers = (() => {
    const parId = new Map<string, string>();
    couriers.forEach(c => parId.set(c.id, c.name));
    routes.forEach(r => {
      const id = r.courier?.id || r.vendor_id || r.driver?.id;
      const nom = nomDuLivreur(r);
      if (id && nom && !parId.has(id)) parId.set(id, nom);
    });
    return Array.from(parId.entries()).map(([id, name]) => ({ id, name }));
  })();

  const getStatusBadge = (status: string) => {
    const badges = {
      ASSIGNED: { label: 'Assignée', className: 'bg-yellow-100 text-yellow-800', icon: Clock },
      STARTED: { label: 'Démarrée', className: 'bg-blue-100 text-blue-800', icon: Truck },
      IN_PROGRESS: { label: 'En cours', className: 'bg-blue-100 text-blue-800', icon: Truck },
      COMPLETED: { label: 'Terminée', className: 'bg-green-100 text-green-800', icon: CheckCircle },
      CANCELLED: { label: 'Annulée', className: 'bg-red-100 text-red-800', icon: XCircle },
    };

    const badge = badges[status as keyof typeof badges] || badges.ASSIGNED;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${badge.className}`}>
        <Icon className="w-3.5 h-3.5" />
        {badge.label}
      </span>
    );
  };

  // Afficher le loader si on n'a pas encore de tenant ou si on charge
  if (!currentTenant || loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="text-gray-500">
            {!currentTenant ? 'Chargement du tenant...' : 'Chargement des tournées...'}
          </p>
        </div>
      </div>
    );
  }

  const stopsAffiches = draftStops ?? selectedRoute?.orders ?? [];
  const ordreModifie = draftStops !== null;
  // Une tournée se modifie tant qu'elle n'est ni terminée ni annulée (même règle que l'API).
  const tourneeModifiable = !!selectedRoute && !['COMPLETED', 'CANCELLED'].includes(selectedRoute.status);
  const autresTournees = selectedRoute
    ? routes.filter((r) =>
        r.id !== selectedRoute.id &&
        !['COMPLETED', 'CANCELLED'].includes(r.status) &&
        String(r.delivery_date).slice(0, 10) === String(selectedRoute.delivery_date).slice(0, 10))
    : [];

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Truck className="w-7 h-7 sm:w-8 sm:h-8 text-green-600" />
            Livraisons Clients
          </h1>
          <p className="text-gray-600 mt-1">
            Gestion des tournées et optimisation d'itinéraires
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <select
            value={newRouteCourierId}
            onChange={(e) => setNewRouteCourierId(e.target.value)}
            className="w-full sm:w-auto px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
            title="Livreur de la tournée à générer"
          >
            <option value="">Livreur à définir plus tard</option>
            {couriers.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <button
            onClick={() => setBonsEnfactOuvert(true)}
            className="flex-1 sm:flex-none justify-center flex items-center gap-2 px-5 py-2.5 bg-sky-700 text-white rounded-lg hover:bg-sky-800 transition-colors font-medium shadow-sm"
            title="Importer les bons de livraison Enfact du jour affiché"
          >
            <FileText className="w-5 h-5" />
            Bons Enfact
          </button>

          <button
            onClick={() => setIrsiaOuvert(true)}
            className="flex-1 sm:flex-none justify-center flex items-center gap-2 px-5 py-2.5 bg-white text-sky-800 border-2 border-sky-700 rounded-lg hover:bg-sky-50 transition-colors font-medium shadow-sm"
            title="Créer les bons Enfact depuis le PDF des commandes IRSIA"
          >
            <FileText className="w-5 h-5" />
            Commandes IRSIA
          </button>

          <button
            onClick={() => setNouvelleTourneeOuverte(true)}
            className="flex-1 sm:flex-none justify-center flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-sm"
          >
            <Plus className="w-5 h-5" />
            Nouvelle tournée
          </button>
        </div>
      </div>

      <NouvelleTourneeModal
        open={nouvelleTourneeOuverte}
        date={selectedDate}
        couriers={couriers}
        livreurParDefaut={newRouteCourierId}
        apiFetch={apiFetch}
        onClose={() => setNouvelleTourneeOuverte(false)}
        onCree={() => { setNouvelleTourneeOuverte(false); loadRoutes(); }}
      />

      <BonsEnfactModal
        open={bonsEnfactOuvert}
        date={selectedDate}
        onClose={() => setBonsEnfactOuvert(false)}
        onImporte={() => loadRoutes()}
      />

      {controlePesee && (
        <ControlePeseeModal
          orderId={controlePesee.orderId}
          onClose={(change) => {
            const d = controlePesee.deliveryId;
            setControlePesee(null);
            if (change) void refreshSelected(d);
          }}
        />
      )}
      <CommandesIrsiaModal
        open={irsiaOuvert}
        onClose={() => setIrsiaOuvert(false)}
        onOuvrirBons={() => setBonsEnfactOuvert(true)}
      />

      {couriers.length === 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-amber-800 text-sm">
          Aucun vendeur ne porte le poste « Livraison ». Cochez-le dans Équipe pour pouvoir confier une tournée.
        </div>
      )}

      {/* Navigation par date et filtres */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Navigation par date */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => changeDate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Jour précédent"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg">
              <Calendar className="w-5 h-5 text-gray-600" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-transparent border-none focus:outline-none font-medium text-gray-900"
              />
            </div>
            <button
              onClick={() => changeDate(1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Jour suivant"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
              className="px-3 py-2 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors font-medium"
            >
              Aujourd'hui
            </button>
          </div>

          {/* Filtres */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-600" />
              <span className="text-sm text-gray-600">Filtres:</span>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
            >
              <option value="all">Tous les statuts</option>
              <option value="ASSIGNED">Assignées</option>
              <option value="STARTED">Démarrées</option>
              <option value="IN_PROGRESS">En cours</option>
              <option value="COMPLETED">Terminées</option>
              <option value="CANCELLED">Annulées</option>
            </select>

            {filterCouriers.length > 0 && (
              <select
                value={courierFilter}
                onChange={(e) => setCourierFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              >
                <option value="all">Tous les livreurs</option>
                {filterCouriers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Statistiques : deux par ligne sur téléphone (empilées, elles prenaient un écran entier) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total tournées</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.todayTotal}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <Truck className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">En cours</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{stats.todayInProgress}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <Navigation className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">En attente</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.todayPending}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Terminées</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats.todayCompleted}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Liste des tournées */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Tournées du {new Date(selectedDate).toLocaleDateString('fr-FR', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </h2>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredRoutes.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">
                {routes.length === 0 ? 'Aucune tournée pour cette date' : 'Aucune tournée ne correspond aux filtres'}
              </p>
              <p className="text-gray-400 text-sm mt-1">
                {routes.length === 0
                  ? 'Cliquez sur "Générer tournées" pour créer automatiquement les tournées'
                  : 'Essayez de modifier les filtres ci-dessus'
                }
              </p>
            </div>
          ) : (
            filteredRoutes.map((route) => (
              <div key={route.id} className="px-6 py-5 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="bg-green-100 p-3 rounded-lg">
                      <Truck className="w-6 h-6 text-green-600" />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Tournée #{route.id.slice(0, 8)}
                        </h3>
                        {getStatusBadge(route.status)}
                      </div>

                      <div className="flex items-center gap-6 text-sm text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-4 h-4" />
                          <span>Livreur: {nomDuLivreur(route) || 'non assigné'}</span>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <Package className="w-4 h-4" />
                          <span>
                            {route.orders.filter(o => o.status === 'DELIVERED').length} / {route.orders.length} arrêts
                          </span>
                        </div>

                        {route.route?.total_duration ? (
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4" />
                            <span>{Math.round(route.route.total_duration)} min</span>
                          </div>
                        ) : null}

                        {route.route?.total_distance ? (
                          <div className="flex items-center gap-1.5">
                            <Navigation className="w-4 h-4" />
                            <span>{route.route.total_distance.toFixed(1)} km</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => exportRoutePDF(route)}
                      className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium flex items-center gap-2"
                      title="Exporter en PDF"
                    >
                      <FileText className="w-4 h-4" />
                      PDF
                    </button>
                    <button
                      onClick={() => openRoute(route)}
                      className="px-4 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors font-medium"
                    >
                      Voir détails
                    </button>
                  </div>
                </div>

                {/* Barre de progression */}
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${route.orders.length > 0 ? (route.orders.filter(o => o.status === 'DELIVERED').length / route.orders.length) * 100 : 0}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal des détails de la tournée */}
      {selectedRoute && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Tournée #{selectedRoute.id.slice(0, 8)}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {new Date(selectedRoute.delivery_date).toLocaleDateString('fr-FR', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <button
                onClick={closeRoute}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Infos générales */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Statut</p>
                  <div className="mt-2">{getStatusBadge(selectedRoute.status)}</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Livreur</p>
                  <select
                    value={selectedRoute.courier?.id || selectedRoute.vendor_id || ''}
                    onChange={(e) => assignCourier(selectedRoute.id, e.target.value)}
                    className="mt-1 w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Non assigné</option>
                    {couriers.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                    {/* Ancien livreur : lisible, mais non réassignable. */}
                    {selectedRoute.courier && !couriers.some(c => c.id === selectedRoute.courier!.id) && (
                      <option value={selectedRoute.courier.id}>{selectedRoute.courier.name}</option>
                    )}
                  </select>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Distance</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {selectedRoute.route?.total_distance
                      ? `${selectedRoute.route.total_distance.toFixed(1)} km`
                      : 'N/A'}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Durée estimée</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {selectedRoute.route?.total_duration
                      ? `${Math.round(selectedRoute.route.total_duration)} min`
                      : 'N/A'}
                  </p>
                </div>
              </div>

              {reoptimizeResult && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
                  <div className="font-semibold">
                    {reoptimizeResult.ordered} arrêt(s) réordonné(s) — {reoptimizeResult.distance_km.toFixed(1)} km, {reoptimizeResult.duration_minutes} min
                  </div>
                  {reoptimizeResult.unlocated > 0 && (
                    <div className="mt-2 text-amber-800">
                      ⚠️ {reoptimizeResult.unlocated} arrêt(s) sans coordonnées : placés en fin de parcours,
                      leur position n'a pas pu être optimisée. Vérifiez l'adresse du client.
                    </div>
                  )}
                </div>
              )}

              {/* Liste des arrêts */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Arrêts ({stopsAffiches.length})
                  </h3>

                  <div className="flex items-center gap-2">
                    {ordreModifie && (
                      <>
                        <button
                          onClick={() => setDraftStops(null)}
                          className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium flex items-center gap-2"
                        >
                          <Undo2 className="w-4 h-4" />
                          Annuler
                        </button>
                        <button
                          onClick={() => saveStopOrder(selectedRoute.id, stopsAffiches)}
                          disabled={savingOrder}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 text-sm font-medium flex items-center gap-2"
                        >
                          <Save className="w-4 h-4" />
                          {savingOrder ? 'Enregistrement...' : 'Enregistrer l\'ordre'}
                        </button>
                      </>
                    )}
                    {!ordreModifie && (
                      <button
                        onClick={() => reoptimize(selectedRoute.id)}
                        disabled={reoptimizing}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 text-sm font-medium flex items-center gap-2"
                        title="Recalculer l'ordre de passage des arrêts restants"
                      >
                        <Wand2 className="w-4 h-4" />
                        {reoptimizing ? 'Calcul...' : 'Réoptimiser'}
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  {stopsAffiches.map((orderItem, index) => (
                    <div
                      key={orderItem.id}
                      className="flex flex-wrap sm:flex-nowrap items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-semibold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-gray-900">
                            {orderItem.order.customer_name}
                          </p>
                          <span className="text-xs text-gray-500">
                            #{orderItem.order.order_number}
                          </span>
                          {heure(orderItem.actual_time) ? (
                            <span className="text-xs font-medium text-green-700">
                              livré à {heure(orderItem.actual_time)}
                            </span>
                          ) : heure(orderItem.estimated_time) ? (
                            <span className="text-xs font-medium text-blue-700">
                              passage ~ {heure(orderItem.estimated_time)}
                            </span>
                          ) : null}
                          {(orderItem.order as any).order_type === 'enfact_bl' && (() => {
                            const meta = orderItem.order.metadata || {};
                            const n = meta.caisses_preparees === undefined ? null : Number(meta.caisses_preparees);
                            const ev = meta.enfact_vidanges_livrees;
                            return (
                              <span className="inline-flex flex-wrap items-center gap-1.5 text-xs">
                                <span className="font-medium text-sky-800 bg-sky-50 rounded px-1.5">
                                  {n === null ? 'Caisses EPS : non notées' : `${n} caisse(s) EPS préparée(s)`}
                                </span>
                                {(() => {
                                  const cp = meta.controle_pesee;
                                  const st = cp?.verdict?.statut;
                                  const libelle = !cp ? '⚖️ Pesée : à faire'
                                    : cp.valide && st === 'ok' ? '⚖️ Pesée OK'
                                    : cp.valide ? `⚖️ Pesée validée avec écart (${cp.motif ?? ''})`
                                    : st === 'trop_leger' ? '⚖️ Trop léger'
                                    : '⚖️ Pesée en cours';
                                  const classe = !cp ? 'text-gray-700 bg-gray-100'
                                    : cp.valide && st === 'ok' ? 'text-emerald-800 bg-emerald-50'
                                    : st === 'trop_leger' && !cp.valide ? 'text-red-800 bg-red-50'
                                    : 'text-amber-800 bg-amber-50';
                                  return (
                                    <button
                                      type="button"
                                      onClick={() => setControlePesee({ orderId: orderItem.order.id, deliveryId: selectedRoute!.id })}
                                      className={`font-medium rounded px-2.5 min-h-11 text-sm sm:min-h-0 sm:px-1.5 sm:text-xs ${classe} hover:underline`}
                                      title="Contrôle pesé avant le départ"
                                    >
                                      {libelle}
                                    </button>
                                  );
                                })()}
                                {ev?.ecrit_le && <span className="text-emerald-700">✅ Vidanges sur le bon {ev.bon}</span>}
                                {ev?.erreur && <span className="text-red-700" title={ev.erreur}>⚠️ pas sur le bon : {ev.erreur}</span>}
                                <button
                                  type="button"
                                  onClick={() => caissesPreparees(orderItem.order.id, selectedRoute!.id, n)}
                                  className="rounded border border-gray-300 px-1.5 text-gray-700 hover:bg-gray-100"
                                >
                                  {n === null ? 'Noter' : 'Modifier'}
                                </button>
                              </span>
                            );
                          })()}
                          {(orderItem.order as any).order_type === 'enfact_bl' && orderItem.status === 'DELIVERED' && (() => {
                            const meta = orderItem.order.metadata || {};
                            const n = meta.caisses_eps_reprises === undefined ? null : Number(meta.caisses_eps_reprises);
                            const ev = meta.enfact_vidanges;
                            return (
                              <span className="inline-flex flex-wrap items-center gap-1.5 text-xs">
                                <span className="font-medium text-amber-800 bg-amber-50 rounded px-1.5">
                                  {n === null ? 'Vidanges EPS : non notées' : `${n} vidange(s) EPS reprise(s)`}
                                </span>
                                {ev?.ecrit_le && <span className="text-emerald-700">✅ sur le bon {ev.bon}</span>}
                                {ev?.erreur && <span className="text-red-700" title={ev.erreur}>⚠️ pas sur le bon : {ev.erreur}</span>}
                                <button
                                  type="button"
                                  onClick={() => vidangesEps(orderItem.order.id, selectedRoute!.id, n)}
                                  className="rounded border border-gray-300 px-1.5 text-gray-700 hover:bg-gray-100"
                                >
                                  Modifier
                                </button>
                                {ev?.erreur && (
                                  <button
                                    type="button"
                                    onClick={() => vidangesEps(orderItem.order.id, selectedRoute!.id, n, true)}
                                    className="rounded border border-gray-300 px-1.5 text-gray-700 hover:bg-gray-100"
                                  >
                                    Réécrire sur le bon
                                  </button>
                                )}
                              </span>
                            );
                          })()}
                        </div>
                        {orderItem.order.delivery_address && (
                          <div className="flex items-start gap-1.5 text-sm text-gray-600 mb-1">
                            <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>
                              {orderItem.order.delivery_address.street}, {orderItem.order.delivery_address.postal_code} {orderItem.order.delivery_address.city}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <span>{(Number(orderItem.order.total_cents) / 100).toFixed(2)} €</span>
                          {orderItem.order.customer_phone && (
                            <span>📞 {orderItem.order.customer_phone}</span>
                          )}
                        </div>
                      </div>

                      {/* Deux boutons plutôt qu'un glisser-déposer : la page se
                          consulte au doigt, sur la tablette du comptoir. */}
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => moveStop(index, -1)}
                          disabled={index === 0}
                          className="w-11 h-11 flex items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-white"
                          title="Monter cet arrêt"
                        >
                          <ArrowUp className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => moveStop(index, 1)}
                          disabled={index === stopsAffiches.length - 1}
                          className="w-11 h-11 flex items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-white"
                          title="Descendre cet arrêt"
                        >
                          <ArrowDown className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Répartir entre livreurs : seulement un arrêt pas encore commencé,
                          et pas pendant qu'on remanie l'ordre (deux brouillons à la fois). */}
                      {tourneeModifiable && !ordreModifie && (orderItem.status ?? 'PENDING') === 'PENDING' && (
                        <div className="flex flex-col gap-1 w-40">
                          {autresTournees.length > 0 && (
                            <select
                              value=""
                              disabled={arretEnCours === orderItem.id}
                              onChange={(e) => e.target.value && modifierArret(selectedRoute.id, orderItem.id, e.target.value)}
                              className="h-11 px-2 border border-gray-300 rounded-lg bg-white text-sm"
                              title="Confier cet arrêt à une autre tournée du même jour"
                            >
                              <option value="">Déplacer vers…</option>
                              {autresTournees.map((r) => (
                                <option key={r.id} value={r.id}>{nomDuLivreur(r) || 'tournée sans livreur'} ({r.orders.length})</option>
                              ))}
                            </select>
                          )}
                          <button
                            onClick={() => confirm(`Retirer ${orderItem.order.customer_name} de cette tournée ? La commande restera prête, à placer ailleurs.`) && modifierArret(selectedRoute.id, orderItem.id, null)}
                            disabled={arretEnCours === orderItem.id}
                            className="h-11 px-2 border border-gray-300 rounded-lg bg-white text-sm text-red-700 hover:bg-red-50"
                          >
                            Retirer
                          </button>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => imprimerEtiquettes(selectedRoute, orderItem.id)}
                        disabled={impressionEtiquettes}
                        className="h-11 px-3 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 shrink-0"
                        title="Imprimer l'étiquette de cet arrêt seulement, sur la Zebra de ce poste"
                      >
                        🏷️ Étiquette
                      </button>

                      <div className="pt-2">
                        {orderItem.status === 'DELIVERED' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  ))}
                  {tourneeModifiable && !ordreModifie && (
                    <AjouterCommandesTournee
                      deliveryId={selectedRoute.id}
                      date={String(selectedRoute.delivery_date).slice(0, 10)}
                      apiFetch={apiFetch}
                      onAjoute={() => { void refreshSelected(selectedRoute.id); loadRoutes(); }}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-between gap-3">
              <div className="flex gap-2">
                <button
                  onClick={() => imprimerEtiquettes(selectedRoute)}
                  disabled={impressionEtiquettes || selectedRoute.orders.length === 0}
                  className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium flex items-center gap-2 disabled:opacity-50"
                  title="Une étiquette 50×25 par livraison, sur la Zebra de ce poste"
                >
                  🏷️ {impressionEtiquettes ? 'Impression…' : 'Étiquettes Zebra'}
                </button>
                <button
                  onClick={() => exportRoutePDF(selectedRoute)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Exporter PDF
                </button>

                {selectedRoute.status === 'ASSIGNED' && (
                  <button
                    onClick={() => startDelivery(selectedRoute.id)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
                  >
                    <Truck className="w-4 h-4" />
                    Démarrer la tournée
                  </button>
                )}
                {(selectedRoute.status === 'STARTED' || selectedRoute.status === 'IN_PROGRESS') && (
                  <button
                    onClick={() => completeDelivery(selectedRoute.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Terminer la tournée
                  </button>
                )}
                {selectedRoute.status !== 'COMPLETED' && selectedRoute.status !== 'CANCELLED' && (
                  <button
                    onClick={() => cancelDelivery(selectedRoute.id)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Annuler
                  </button>
                )}
              </div>
              <button
                onClick={closeRoute}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
